import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Info, Loader2, TriangleAlert } from "lucide-react";
import { FaCalendarXmark, FaRegEye } from "react-icons/fa6";
import type { CampaignDto, CampaignRecipientListItem } from "@multi-chat/shared";
import { cancelCampaignSchedule, getCampaignReview } from "../../api/campaigns-api.js";
import { getConnection } from "../../api/whatsapp-api.js";
import { useNotifications } from "../notifications/notification-context.js";
import { Button } from "../../components/ui/button/index.js";
import { senderDisplayName, usableWhatsAppSenders } from "./campaign-message-presentation.js";
import { campaignFailureMessage, mapCampaignError } from "./campaigns-errors.js";
import {
  formatCampaignScheduleInstant,
  formatCampaignScheduleInstantParts,
} from "./campaign-review-presentation.js";
import {
  campaignAudienceActionBaseClass,
  campaignAudiencePrimaryActionClass,
} from "./CampaignManageRecipientsButton.js";
import { CampaignPostLaunchOverview } from "./CampaignPostLaunchOverview.js";
import { CampaignPreviewDialog } from "./CampaignPreviewDialog.js";
import { CampaignResultsPanel } from "./CampaignResultsPanel.js";
import { shouldShowCampaignResults } from "./use-campaign-results.js";

export function CampaignPostLaunchWorkspace({
  organizationId,
  campaign,
  recipients,
  canMutate,
  onCampaignChanged,
}: {
  organizationId: number;
  campaign: CampaignDto;
  recipients: readonly CampaignRecipientListItem[];
  canMutate: boolean;
  onCampaignChanged?: () => void | Promise<void>;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { pushNotification } = useNotifications();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [senderName, setSenderName] = useState<string | null>(null);
  const [senderPhone, setSenderPhone] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<unknown>(null);

  useEffect(() => {
    const controller = new AbortController();
    void getCampaignReview(organizationId, campaign.id, controller.signal)
      .then((review) => {
        if (!controller.signal.aborted) setTimezone(review.organizationTimezone);
      })
      .catch(() => {
        if (!controller.signal.aborted) setTimezone("UTC");
      });
    return () => controller.abort();
  }, [organizationId, campaign.id]);

  useEffect(() => {
    if (campaign.status !== "SCHEDULED" || campaign.platformEndpointId == null) return;
    const controller = new AbortController();
    void getConnection(organizationId, controller.signal)
      .then((connection) => {
        if (controller.signal.aborted) return;
        const match = usableWhatsAppSenders(connection).find(
          (phone) => phone.platformEndpointId === campaign.platformEndpointId,
        );
        setSenderName(match ? senderDisplayName(match) : null);
        setSenderPhone(match?.phoneNumber ?? null);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setSenderName(null);
          setSenderPhone(null);
        }
      });
    return () => controller.abort();
  }, [campaign.platformEndpointId, campaign.status, organizationId]);

  const zone = timezone ?? "UTC";
  const scheduledLabel =
    campaign.scheduledAt != null
      ? formatCampaignScheduleInstant(campaign.scheduledAt, zone, i18n.language)
      : null;
  const startedLabel =
    campaign.startedAt != null
      ? formatCampaignScheduleInstant(campaign.startedAt, zone, i18n.language)
      : null;
  const completedLabel =
    campaign.completedAt != null
      ? formatCampaignScheduleInstant(campaign.completedAt, zone, i18n.language)
      : null;

  const cancelSchedule = async () => {
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelCampaignSchedule(organizationId, campaign.id);
      pushNotification({ message: t("campaigns.toasts.scheduleCancelled") });
      await onCampaignChanged?.();
    } catch (caught) {
      setCancelError(caught);
      pushNotification({
        kind: "error",
        message: mapCampaignError(t, caught, t("campaigns.errors.requestFailed")),
      });
    } finally {
      setCancelling(false);
    }
  };

  const showResults = shouldShowCampaignResults(campaign.status);
  const cancelErrorMessage = cancelError
    ? mapCampaignError(t, cancelError, t("campaigns.errors.requestFailed"))
    : null;

  const scheduled = campaign.status === "SCHEDULED";
  const sendInstant = scheduled
    ? campaign.scheduledAt
    : (campaign.startedAt ?? campaign.scheduledAt);
  const sendTime =
    sendInstant != null && timezone != null
      ? formatCampaignScheduleInstantParts(sendInstant, timezone, i18n.language)
      : null;
  const subtitle = (() => {
    switch (campaign.status) {
      case "SCHEDULED":
        return scheduledLabel ? t("campaigns.results.scheduledFor", { when: scheduledLabel }) : null;
      case "RUNNING":
      case "FAILED":
        return startedLabel ? t("campaigns.results.startedAt", { when: startedLabel }) : null;
      case "COMPLETED":
        return completedLabel ? t("campaigns.results.completedAt", { when: completedLabel }) : null;
      case "CANCELLED":
      case "DRAFT":
        return null;
      default: {
        const _exhaustive: never = campaign.status;
        return _exhaustive;
      }
    }
  })();

  const previewButton = (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className={campaignAudienceActionBaseClass}
      onClick={() => setPreviewOpen(true)}
      data-testid="campaign-preview"
    >
      <FaRegEye size={14} className="shrink-0" aria-hidden="true" />
      {t("campaigns.results.preview")}
    </Button>
  );

  const overview =
    campaign.status === "DRAFT" ? null : (
      <CampaignPostLaunchOverview
        status={campaign.status}
        subtitle={subtitle}
        recipientCount={campaign.audienceSnapshotSummary?.readyRecipients ?? 0}
        showSender={scheduled}
        senderName={senderName}
        senderPhone={senderPhone}
        templateName={campaign.templateSnapshot.name ?? null}
        sendTime={sendTime}
        timezone={timezone}
        actions={
          <>
            {previewButton}
            {scheduled && canMutate ? (
              <Button
                type="button"
                size="sm"
                className={campaignAudiencePrimaryActionClass}
                disabled={cancelling}
                onClick={() => void cancelSchedule()}
                data-testid="campaign-cancel-schedule"
              >
                {cancelling ? (
                  <>
                    <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                    {t("campaigns.review.launch.cancelling")}
                  </>
                ) : (
                  <>
                    <FaCalendarXmark size={13} className="shrink-0" aria-hidden="true" />
                    {t("campaigns.review.launch.cancelSchedule")}
                  </>
                )}
              </Button>
            ) : null}
          </>
        }
      >
        {cancelErrorMessage ? (
          <p className="m-0 text-[0.75rem] text-error" role="alert">
            {cancelErrorMessage}
          </p>
        ) : null}

        {scheduled ? (
          <p className="m-0 flex items-start gap-2 text-[0.75rem] leading-relaxed text-muted">
            <Info size={13} className="mt-0.5 shrink-0" aria-hidden="true" />
            {t("campaigns.results.scheduledEmptyBody")}
          </p>
        ) : null}

        {campaign.status === "FAILED" ? (
          <div
            className="grid gap-1 rounded-lg border border-error/30 bg-error-muted px-3 py-2 text-error"
            data-testid="campaign-failed-state"
            role="alert"
          >
            <p className="m-0 flex items-center gap-1.5 text-[0.8125rem] font-semibold">
              <TriangleAlert size={14} aria-hidden="true" />
              {t("campaigns.results.campaignFailed")}
            </p>
            <p className="m-0 text-[0.8125rem]">{campaignFailureMessage(t, campaign.failureCode)}</p>
          </div>
        ) : null}

        {campaign.status === "CANCELLED" ? (
          <p className="m-0 text-[0.75rem] text-muted" data-testid="campaign-cancelled-state">
            {t("campaigns.results.cancelledEmptyBody")}
          </p>
        ) : null}
      </CampaignPostLaunchOverview>
    );

  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-4 max-lg:overflow-y-auto"
      data-testid="campaign-post-launch-workspace"
    >
      {scheduled ? (
        <div className="min-h-0 flex-1 overflow-y-auto" data-testid="campaign-scheduled-state">
          <div className="mx-auto w-full max-w-[49rem] pt-4 pb-6 sm:pt-10">{overview}</div>
        </div>
      ) : (
        overview
      )}

      {showResults ? (
        <div
          className="flex min-h-0 flex-1 flex-col max-lg:flex-none"
          data-testid={
            campaign.status === "RUNNING"
              ? "campaign-running-state"
              : campaign.status === "COMPLETED"
                ? "campaign-completed-state"
                : "campaign-results-host"
          }
        >
          <CampaignResultsPanel organizationId={organizationId} campaign={campaign} />
        </div>
      ) : null}

      <CampaignPreviewDialog
        open={previewOpen}
        organizationId={organizationId}
        campaign={campaign}
        recipients={recipients}
        onOpenChange={setPreviewOpen}
      />
    </div>
  );
}
